import { useState, useEffect, useRef } from 'react';
import { useLang } from '../../context/LanguageContext';
import {
  verifyAuth, login, logout,
  getPhotos, uploadPhoto, reorderPhotos, deletePhoto,
  getResume, createResumeEntry, updateResumeEntry, deleteResumeEntry,
  getVideos, uploadVideoFile, deleteVideo, reorderVideos,
  getHeroProfile, updateHeroProfile,
} from '../../services/api';
import styles from './AdminPage.module.css';

export default function AdminPage() {
  const { t } = useLang();
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    verifyAuth()
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <div className={styles.loading}>…</div>;
  if (!authed) return <LoginView onSuccess={() => setAuthed(true)} t={t} />;
  return <ManagerView onLogout={() => setAuthed(false)} t={t} />;
}

// ── LOGIN ──────────────────────────────────────────────────────
function LoginView({ onSuccess, t }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.ok) onSuccess();
      else setError(res.error || t('admin_login_error'));
    } catch {
      setError(t('admin_login_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.loginBox}>
        <h1 className={styles.loginTitle}>{t('admin_login_title')}</h1>
        <form onSubmit={handleSubmit} className={styles.loginForm}>
          <label className={styles.label}>{t('admin_email')}</label>
          <input className={styles.input} type="email" autoComplete="email"
            value={email} onChange={e => setEmail(e.target.value)} required />
          <label className={styles.label}>{t('admin_password')}</label>
          <input className={styles.input} type="password" autoComplete="current-password"
            value={password} onChange={e => setPassword(e.target.value)} required />
          {error && <p className={styles.error}>{error}</p>}
          <button className={styles.btn} type="submit" disabled={loading}>
            {loading ? t('admin_logging_in') : t('admin_login_btn')}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── CONFIRM DIALOG ────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className={styles.confirmOverlay}>
      <div className={styles.confirmBox}>
        <p className={styles.confirmMessage}>{message}</p>
        <div className={styles.confirmBtns}>
          <button className={`${styles.btn} ${styles.btnDanger}`} onClick={onConfirm}>Delete</button>
          <button className={`${styles.btn} ${styles.btnGhost}`} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── MANAGER ───────────────────────────────────────────────────
function ManagerView({ onLogout, t }) {
  const [tab, setTab] = useState('titlecard');
  const [resume, setResume] = useState({
    film: [], tv: [], theatre: [], commercial: [], training: [], skill: [], dialect: [],
  });
  const [confirmDialog, setConfirmDialog] = useState(null); // { message, onConfirm }
  const [heroProfile, setHeroProfile] = useState({});

  // Photos state
  const [photos, setPhotos] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');

  // Videos state
  const [videos, setVideos] = useState([]);
  const [videoDragging, setVideoDragging] = useState(null);
  const [videoSaveStatus, setVideoSaveStatus] = useState('');
  const [preview, setPreview] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    getPhotos().then(setPhotos).catch(() => {});
    getResume().then(data => {
      if (data && typeof data === 'object') setResume(prev => ({ ...prev, ...data }));
    }).catch(() => {});
    getVideos().then(setVideos).catch(() => {});
    getHeroProfile().then(setHeroProfile).catch(() => {});
  }, []);

  const handleLogout = async () => { await logout(); onLogout(); };

  const confirm = (message, onConfirm) => setConfirmDialog({ message, onConfirm });

  // ── Photo drag handlers
  const handleDragStart = (e, idx) => { setDragging(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragging === null || dragging === idx) return;
    const reordered = [...photos];
    reordered.splice(idx, 0, reordered.splice(dragging, 1)[0]);
    setPhotos(reordered);
    setDragging(idx);
    setSaveStatus('');
  };
  const handleDragEnd = () => setDragging(null);

  const handleSaveOrder = async () => {
    setSaveStatus('saving');
    try { await reorderPhotos(photos.map(p => p._id)); setSaveStatus('saved'); }
    catch { setSaveStatus('error'); }
  };

  const handleDeletePhoto = photo => {
    confirm(`Delete photo? This cannot be undone.`, async () => {
      setConfirmDialog(null);
      try {
        await deletePhoto(photo._id);
        setPhotos(prev => prev.filter(p => p._id !== photo._id));
      } catch { alert(t('admin_delete_error')); }
    });
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setUploadError('');
    const reader = new FileReader();
    reader.onload = ev => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError('');
    try {
      const newPhoto = await uploadPhoto(uploadFile);
      setPhotos(prev => [...prev, newPhoto]);
      setUploadFile(null);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setUploadError(err?.error || t('admin_upload_error'));
    } finally {
      setUploading(false);
    }
  };

  const saveLabel =
    saveStatus === 'saving' ? t('admin_saving')
    : saveStatus === 'saved' ? t('admin_saved')
    : saveStatus === 'error' ? t('admin_save_error')
    : t('admin_save_order');

  // Helper to update one section of resume state
  const setSection = (type, updater) =>
    setResume(prev => ({ ...prev, [type]: typeof updater === 'function' ? updater(prev[type]) : updater }));

  // ── Video drag handlers
  const handleVideoDragStart = (e, idx) => { setVideoDragging(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleVideoDragOver = (e, idx) => {
    e.preventDefault();
    if (videoDragging === null || videoDragging === idx) return;
    const reordered = [...videos];
    reordered.splice(idx, 0, reordered.splice(videoDragging, 1)[0]);
    setVideos(reordered);
    setVideoDragging(idx);
    setVideoSaveStatus('');
  };
  const handleVideoDragEnd = () => setVideoDragging(null);

  const handleVideoSaveOrder = async () => {
    setVideoSaveStatus('saving');
    try { await reorderVideos(videos.map(v => v._id)); setVideoSaveStatus('saved'); }
    catch { setVideoSaveStatus('error'); }
  };

  const handleDeleteVideo = video => {
    confirm(`Delete "${video.title || 'this video'}"? This cannot be undone.`, async () => {
      setConfirmDialog(null);
      try {
        await deleteVideo(video._id);
        setVideos(prev => prev.filter(v => v._id !== video._id));
      } catch { alert('Delete failed. Please try again.'); }
    });
  };

  const videoSaveLabel =
    videoSaveStatus === 'saving' ? t('admin_saving')
    : videoSaveStatus === 'saved' ? t('admin_saved')
    : videoSaveStatus === 'error' ? t('admin_save_error')
    : t('admin_save_order');

  const TABS = [
    { id: 'titlecard', label: 'Title Card' },
    { id: 'photos', label: 'Photos' },
    { id: 'credits', label: 'Credits' },
    { id: 'training', label: 'Training' },
    { id: 'skills', label: 'Skills & Dialects' },
    { id: 'videos', label: 'Videos' },
  ];

  return (
    <div className={styles.root}>
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      <header className={styles.header}>
        <span className={styles.wordmark}>joaquin@admin ~ %</span>
        <button className={styles.logoutBtn} onClick={handleLogout}>{t('admin_logout')}</button>
      </header>

      <nav className={styles.tabBar}>
        {TABS.map(tb => (
          <button key={tb.id}
            className={`${styles.tabBtn} ${tab === tb.id ? styles.tabBtnActive : ''}`}
            onClick={() => setTab(tb.id)}>
            {tb.label}
          </button>
        ))}
      </nav>

      <main className={styles.main}>
        {/* ── TITLE CARD TAB ── */}
        {tab === 'titlecard' && (
          <TitleCardSection
            heroProfile={heroProfile}
            onChange={setHeroProfile}
          />
        )}

        {/* ── PHOTOS TAB ── */}
        {tab === 'photos' && (
          <>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>{t('admin_carousel_title')}</h2>
                <button
                  className={`${styles.btn} ${saveStatus === 'saved' ? styles.btnSaved : ''} ${saveStatus === 'error' ? styles.btnError : ''}`}
                  onClick={handleSaveOrder}
                  disabled={saveStatus === 'saving' || photos.length === 0}
                >
                  {saveLabel}
                </button>
              </div>
              <p className={styles.hint}>{t('admin_drag_hint')}</p>
              {photos.length === 0 && <p className={styles.empty}>{t('admin_no_photos')}</p>}
              <div className={styles.grid}>
                {photos.map((photo, idx) => (
                  <div key={photo._id}
                    className={`${styles.card} ${dragging === idx ? styles.cardDragging : ''}`}
                    draggable
                    onDragStart={e => handleDragStart(e, idx)}
                    onDragOver={e => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                  >
                    <span className={styles.position}>{idx + 1}</span>
                    <img src={photo.url} alt={`Headshot ${idx + 1}`} className={styles.thumb} draggable={false} />
                    <button className={styles.deleteBtn} onClick={() => handleDeletePhoto(photo)} title={t('admin_delete')}>✕</button>
                  </div>
                ))}
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('admin_upload_title')}</h2>
              <div className={styles.uploadArea}>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                  className={styles.fileInput} onChange={handleFileChange} id="photo-upload" />
                <label htmlFor="photo-upload" className={styles.fileLabel}>
                  {uploadFile ? uploadFile.name : t('admin_choose_file')}
                </label>
                {preview && <img src={preview} alt="Preview" className={styles.preview} />}
                {uploadError && <p className={styles.error}>{uploadError}</p>}
                <button className={styles.btn} onClick={handleUpload} disabled={!uploadFile || uploading}>
                  {uploading ? t('admin_uploading') : t('admin_upload_btn')}
                </button>
              </div>
            </section>
          </>
        )}

        {/* ── CREDITS TAB ── */}
        {tab === 'credits' && (
          <>
            {[
              { type: 'film',       label: 'Film' },
              { type: 'tv',         label: 'Television' },
              { type: 'theatre',    label: 'Theatre' },
              { type: 'commercial', label: 'Commercial & Voice Over' },
            ].map(({ type, label }) => (
              <ResumeSection
                key={type}
                label={label}
                type={type}
                entries={resume[type]}
                fields={[
                  { key: 'title',  placeholder: 'Title' },
                  { key: 'role',   placeholder: 'Role' },
                  { key: 'detail', placeholder: 'Detail' },
                ]}
                onEntriesChange={updated => setSection(type, updated)}
                confirm={confirm}
                onConfirmClose={() => setConfirmDialog(null)}
              />
            ))}
          </>
        )}

        {/* ── TRAINING TAB ── */}
        {tab === 'training' && (
          <ResumeSection
            label="Training"
            type="training"
            entries={resume.training}
            fields={[
              { key: 'title',  placeholder: 'Workshop / Course' },
              { key: 'coach',  placeholder: 'Coach / Instructor' },
              { key: 'detail', placeholder: 'Location · Duration' },
            ]}
            onEntriesChange={updated => setSection('training', updated)}
            confirm={confirm}
            onConfirmClose={() => setConfirmDialog(null)}
          />
        )}

        {/* ── SKILLS & DIALECTS TAB ── */}
        {tab === 'skills' && (
          <div className={styles.skillsGrid}>
            <ResumeSection
              label="Special Skills"
              type="skill"
              entries={resume.skill}
              fields={[
                { key: 'text_en', placeholder: 'English' },
                { key: 'text_es', placeholder: 'Español' },
              ]}
              onEntriesChange={updated => setSection('skill', updated)}
              confirm={confirm}
              onConfirmClose={() => setConfirmDialog(null)}
            />
            <ResumeSection
              label="Dialects & Accents"
              type="dialect"
              entries={resume.dialect}
              fields={[
                { key: 'text_en', placeholder: 'English' },
                { key: 'text_es', placeholder: 'Español' },
              ]}
              onEntriesChange={updated => setSection('dialect', updated)}
              confirm={confirm}
              onConfirmClose={() => setConfirmDialog(null)}
            />
          </div>
        )}
        {/* ── VIDEOS TAB ── */}
        {tab === 'videos' && (
          <>
            <VideoAddForm onAdd={v => setVideos(prev => [...prev, v])} />
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Reel Videos</h2>
                <button
                  className={`${styles.btn} ${videoSaveStatus === 'saved' ? styles.btnSaved : ''} ${videoSaveStatus === 'error' ? styles.btnError : ''}`}
                  onClick={handleVideoSaveOrder}
                  disabled={videoSaveStatus === 'saving' || videos.length === 0}
                >
                  {videoSaveLabel}
                </button>
              </div>
              <p className={styles.hint}>{t('admin_drag_hint')}</p>
              {videos.length === 0 && <p className={styles.empty}>No videos yet. Add one above.</p>}
              <div className={styles.grid}>
                {videos.map((video, idx) => (
                  <div key={video._id}
                    className={`${styles.card} ${videoDragging === idx ? styles.cardDragging : ''}`}
                    draggable
                    onDragStart={e => handleVideoDragStart(e, idx)}
                    onDragOver={e => handleVideoDragOver(e, idx)}
                    onDragEnd={handleVideoDragEnd}
                  >
                    <span className={styles.position}>{idx + 1}</span>
                    {video.thumbUrl
                      ? <img src={video.thumbUrl} alt={video.title || ''} className={styles.videoThumbAdmin} draggable={false} />
                      : <div className={styles.videoThumbAdmin} style={{ background: 'rgba(0,255,136,0.05)' }} />
                    }
                    <div className={styles.videoCardMeta}>
                      <span className={styles.videoCardTitle}>{video.title || '—'}</span>
                      {video.credit && <span className={styles.videoCardSub}>{video.credit}</span>}
                    </div>
                    <button className={styles.deleteBtn} onClick={() => handleDeleteVideo(video)} title={t('admin_delete')}>✕</button>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

// ── TITLE CARD SECTION ────────────────────────────────────────
function TitleCardSection({ heroProfile, onChange }) {
  const [saveStatus, setSaveStatus] = useState('');

  const set = (key, value) => onChange(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      const updated = await updateHeroProfile(heroProfile);
      onChange(updated);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  };

  const saveLabel =
    saveStatus === 'saving' ? 'Saving…'
    : saveStatus === 'saved' ? 'Saved ✓'
    : saveStatus === 'error' ? 'Save Failed'
    : 'Save All';

  const field = (key, placeholder) => (
    <input
      className={styles.input}
      value={heroProfile[key] || ''}
      placeholder={placeholder}
      onChange={e => { set(key, e.target.value); setSaveStatus(''); }}
    />
  );

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Title Card</h2>
        <button
          className={`${styles.btn} ${saveStatus === 'saved' ? styles.btnSaved : ''} ${saveStatus === 'error' ? styles.btnError : ''}`}
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
        >
          {saveLabel}
        </button>
      </div>

      <div className={styles.entryCard}>
        <label className={styles.label}>Top Highlight</label>
        {field('eyebrow', 'e.g. SAG-AFTRA Eligible · Actor')}
      </div>

      <div className={styles.entryCard}>
        <label className={styles.label}>Name</label>
        <div className={styles.entryFields}>
          {field('firstName', 'First name (italic)')}
          {field('lastName', 'Last name')}
        </div>
      </div>

      <div className={styles.entryCard}>
        <label className={styles.label}>Tagline</label>
        {field('tagline', 'e.g. Film · Television · Theatre · Voice Over')}
      </div>

      <div className={styles.entryCard}>
        <label className={styles.label}>Stats</label>
        <div className={styles.entryFields}>
          {field('stat1Val', 'Stat 1 value (e.g. 6\'2")')}
          {field('stat1Label', 'Stat 1 label (e.g. Height)')}
        </div>
        <div className={styles.entryFields}>
          {field('stat2Val', 'Stat 2 value (e.g. 10)')}
          {field('stat2Label', 'Stat 2 label (e.g. years telling stories)')}
        </div>
        <div className={styles.entryFields}>
          {field('stat3Val', 'Stat 3 value (e.g. Dialect)')}
          {field('stat3Label', 'Stat 3 label (e.g. Expert)')}
        </div>
      </div>

      <div className={styles.entryCard}>
        <label className={styles.label}>Training</label>
        <div className={styles.entryFields}>
          {field('bfaLine1', 'Line 1 (e.g. Bachelor of Fine Arts — Acting)')}
          {field('bfaLine2', 'Line 2 (e.g. Univ. of Central Florida)')}
        </div>
      </div>

      <div className={styles.entryCard}>
        <label className={styles.label}>Agency</label>
        {field('agency', 'e.g. Marta Rubio Agency')}
      </div>
    </section>
  );
}

// ── VIDEO ADD FORM ────────────────────────────────────────────
function VideoAddForm({ onAdd }) {
  const [file, setFile] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [credit, setCredit] = useState('');
  const [director, setDirector] = useState('');
  const [studio, setStudio] = useState('');
  const [thumbTime, setThumbTime] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const progressTimer = useRef(null);

  const handleFileChange = e => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setError('');
    setProgress(0);
    setLocalPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(0);

    // Simulate progress up to 90%
    progressTimer.current = setInterval(() => {
      setProgress(p => (p < 90 ? p + 2 : p));
    }, 400);

    try {
      const v = await uploadVideoFile(file, { title, credit, director, studio, thumbTime: thumbTime || '2' });
      clearInterval(progressTimer.current);
      setProgress(100);
      onAdd(v);
      setFile(null);
      setLocalPreview(null);
      setTitle(''); setCredit(''); setDirector(''); setStudio(''); setThumbTime('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => setProgress(0), 800);
    } catch (err) {
      clearInterval(progressTimer.current);
      setProgress(0);
      setError(err?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Upload Video</h2>
      <form onSubmit={handleSubmit} className={styles.videoForm}>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          className={styles.fileInput}
          id="video-upload"
          onChange={handleFileChange}
        />
        <label htmlFor="video-upload" className={styles.fileLabel}>
          {file ? file.name : 'Choose a video file (MP4, MOV, etc.)…'}
        </label>
        {localPreview && (
          <video src={localPreview} className={styles.videoLocalPreview} controls muted playsInline />
        )}
        {progress > 0 && (
          <div className={styles.uploadProgress}>
            <div className={styles.uploadProgressBar} style={{ width: `${progress}%` }} />
            <span className={styles.uploadProgressLabel}>
              {progress < 100 ? `Uploading… ${progress}%` : 'Processing…'}
            </span>
          </div>
        )}
        <div className={styles.videoFormFields}>
          <input className={styles.input} placeholder="Title" value={title} maxLength={200} onChange={e => setTitle(e.target.value)} />
          <input className={styles.input} placeholder="Credit / Role" value={credit} maxLength={200} onChange={e => setCredit(e.target.value)} />
          <input className={styles.input} placeholder="Director" value={director} maxLength={200} onChange={e => setDirector(e.target.value)} />
          <input className={styles.input} placeholder="Studio / Network" value={studio} maxLength={200} onChange={e => setStudio(e.target.value)} />
        </div>
        <input
          className={styles.input}
          type="number"
          placeholder="Thumbnail at (seconds, e.g. 3)"
          value={thumbTime}
          min="0"
          step="0.5"
          onChange={e => setThumbTime(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        {error && <p className={styles.error}>{error}</p>}
        <button className={styles.btn} type="submit" disabled={uploading || !file}>
          {uploading ? 'Uploading…' : '↑ Upload Video'}
        </button>
      </form>
    </section>
  );
}

// ── RESUME SECTION ────────────────────────────────────────────
// Renders a labelled list of entries with inline edit forms + add button.
function ResumeSection({ label, type, entries, fields, onEntriesChange, confirm, onConfirmClose }) {
  // newEntries: unsaved cards (array of { tempId, fields... })
  const [newEntries, setNewEntries] = useState([]);
  const [saving, setSaving] = useState({}); // { [id]: 'saving'|'saved'|'error' }

  const addNew = () => {
    const tempId = `new_${Date.now()}_${Math.random()}`;
    const blank = { tempId, ...Object.fromEntries(fields.map(f => [f.key, ''])) };
    setNewEntries(prev => [...prev, blank]);
  };

  const removeNew = tempId => setNewEntries(prev => prev.filter(e => e.tempId !== tempId));

  const updateNew = (tempId, key, value) => {
    setNewEntries(prev => prev.map(e => e.tempId === tempId ? { ...e, [key]: value } : e));
  };

  const saveNew = async (entry) => {
    setSaving(prev => ({ ...prev, [entry.tempId]: 'saving' }));
    try {
      const data = { type, ...Object.fromEntries(fields.map(f => [f.key, entry[f.key] || ''])) };
      const created = await createResumeEntry(data);
      onEntriesChange([...entries, created]);
      setNewEntries(prev => prev.filter(e => e.tempId !== entry.tempId));
    } catch {
      setSaving(prev => ({ ...prev, [entry.tempId]: 'error' }));
    }
  };

  const updateExisting = async (entry, key, value) => {
    // Optimistically update local state
    onEntriesChange(entries.map(e => e._id === entry._id ? { ...e, [key]: value } : e));
  };

  const saveExisting = async (entry) => {
    setSaving(prev => ({ ...prev, [entry._id]: 'saving' }));
    try {
      const data = Object.fromEntries(fields.map(f => [f.key, entry[f.key] || '']));
      const updated = await updateResumeEntry(entry._id, data);
      onEntriesChange(entries.map(e => e._id === entry._id ? updated : e));
      setSaving(prev => ({ ...prev, [entry._id]: 'saved' }));
    } catch {
      setSaving(prev => ({ ...prev, [entry._id]: 'error' }));
    }
  };

  const requestDelete = (entry) => {
    const label = entry.title || entry.text_en || 'this entry';
    confirm(`Delete "${label}"? This cannot be undone.`, async () => {
      onConfirmClose();
      try {
        await deleteResumeEntry(entry._id);
        onEntriesChange(entries.filter(e => e._id !== entry._id));
      } catch { alert('Delete failed. Please try again.'); }
    });
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{label}</h2>

      {/* Saved entries */}
      {entries.map(entry => {
        const status = saving[entry._id];
        return (
          <div key={entry._id} className={styles.entryCard}>
            <div className={styles.entryFields}>
              {fields.map(f => (
                <input
                  key={f.key}
                  className={styles.entryInput}
                  value={entry[f.key] || ''}
                  placeholder={f.placeholder}
                  onChange={e => updateExisting(entry, f.key, e.target.value)}
                />
              ))}
            </div>
            <div className={styles.entryActions}>
              <button
                className={`${styles.btn} ${styles.btnSmall} ${status === 'saved' ? styles.btnSaved : ''} ${status === 'error' ? styles.btnError : ''}`}
                onClick={() => saveExisting(entry)}
                disabled={status === 'saving'}
              >
                {status === 'saving' ? '…' : status === 'saved' ? 'Saved' : status === 'error' ? 'Error' : 'Save'}
              </button>
              <button className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`} onClick={() => requestDelete(entry)}>
                Delete
              </button>
            </div>
          </div>
        );
      })}

      {/* New unsaved entries */}
      {newEntries.map(entry => {
        const status = saving[entry.tempId];
        return (
          <div key={entry.tempId} className={`${styles.entryCard} ${styles.entryCardNew}`}>
            <div className={styles.entryFields}>
              {fields.map(f => (
                <input
                  key={f.key}
                  className={styles.entryInput}
                  value={entry[f.key] || ''}
                  placeholder={f.placeholder}
                  onChange={e => updateNew(entry.tempId, f.key, e.target.value)}
                  autoFocus={f === fields[0]}
                />
              ))}
            </div>
            <div className={styles.entryActions}>
              <button
                className={`${styles.btn} ${styles.btnSmall} ${status === 'error' ? styles.btnError : ''}`}
                onClick={() => saveNew(entry)}
                disabled={status === 'saving'}
              >
                {status === 'saving' ? '…' : status === 'error' ? 'Error' : 'Save'}
              </button>
              <button className={`${styles.btn} ${styles.btnSmall} ${styles.btnGhost}`} onClick={() => removeNew(entry.tempId)}>
                ✕
              </button>
            </div>
          </div>
        );
      })}

      <button className={styles.addBtn} onClick={addNew}>+ Add</button>
    </section>
  );
}
